import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IconBase } from '../../assets/images/icons/icon_base.svg';
import { ReactComponent as IconBatteryCharging } from '../../assets/images/icons/icon_battery_charging.svg';
import { ReactComponent as IconCellular } from '../../assets/images/icons/icon_cellular.svg';
import { ReactComponent as IconConnected } from '../../assets/images/icons/icon_connected.svg';
import { ReactComponent as IconDrip } from '../../assets/images/icons/icon_drip.svg';
import { ReactComponent as IconFeather } from '../../assets/images/icons/icon_feather.svg';
import { ReactComponent as IconMagic } from '../../assets/images/icons/icon_magic.svg';
import { ReactComponent as IconReinforced } from '../../assets/images/icons/icon_reinforced.svg';
import { ReactComponent as IconVibration } from '../../assets/images/icons/icon_vibration.svg';
import { ReactComponent as IconCheckCircle } from '../../assets/images/icons/check_circle_filled.svg';
import { ReactComponent as IconStar } from '../../assets/images/icons/star.svg';
import AppPaths from '../../AppPaths';
import { Species } from '../../types/gql-op-types';
import { productListingPage, quizQuestionPage } from '../../lib/analytics/events';
import { useLaunchQuiz } from '../../lib/fiQuiz';
import type { PickYourFiDeviceKey } from './HardwareFirstBelowFold';
import styles from './HardwareFirstSections.module.scss';

const DEVICE_NAMES: Record<PickYourFiDeviceKey, string> = {
  mini: 'Fi Mini',
  s3: 'Fi Series 3+',
  ultra: 'Fi Ultra',
};

interface SpecRow {
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  titleKey: string;
  subKey: string;
}

// row copy follows the figma's per-device spec list; the standalone pdps keep their own strings
const SPEC_ROWS: Record<PickYourFiDeviceKey, SpecRow[]> = {
  mini: [
    {
      Icon: IconDrip,
      titleKey: 'hardware_first_spec_seriously_waterproof',
      subKey: 'hardware_first_spec_ip68_salt_water',
    },
    { Icon: IconFeather, titleKey: 'small_and_versatile', subKey: 'hardware_first_spec_attach_any_collar_sub' },
    {
      Icon: IconBatteryCharging,
      titleKey: 'hardware_first_spec_mini_battery_row',
      subKey: 'hardware_first_spec_mini_battery_sub',
    },
    { Icon: IconMagic, titleKey: 'featuring_fi_intelligence', subKey: 'ask_fi_anything_your_dog_decoded' },
    { Icon: IconCellular, titleKey: 'hardware_first_spec_verizon', subKey: 'hardware_first_spec_verizon_sub' },
  ],
  s3: [
    {
      Icon: IconDrip,
      titleKey: 'hardware_first_spec_seriously_waterproof',
      subKey: 'hardware_first_spec_ip68_66k_salt_water',
    },
    { Icon: IconReinforced, titleKey: 'hardware_first_spec_s3_pull', subKey: 'handles_the_worst_chewers_and_pullers' },
    {
      Icon: IconBatteryCharging,
      titleKey: 'hardware_first_spec_s3_battery_row',
      subKey: 'hardware_first_spec_s3_battery_row_sub',
    },
    { Icon: IconMagic, titleKey: 'featuring_fi_intelligence', subKey: 'hardware_first_spec_s3_intelligence_sub' },
    { Icon: IconCellular, titleKey: 'hardware_first_spec_att', subKey: 'hardware_first_spec_att_sub' },
    {
      Icon: IconConnected,
      titleKey: 'hardware_first_spec_extended_coverage',
      subKey: 'hardware_first_spec_s3_coverage_sub',
    },
  ],
  ultra: [
    {
      Icon: IconDrip,
      titleKey: 'hardware_first_spec_seriously_waterproof',
      subKey: 'hardware_first_spec_ip68_66k_tested',
    },
    {
      Icon: IconVibration,
      titleKey: 'hardware_first_spec_ultra_callback',
      subKey: 'hardware_first_spec_ultra_callback_sub',
    },
    {
      Icon: IconCellular,
      titleKey: 'hardware_first_spec_ultra_remote',
      subKey: 'hardware_first_spec_ultra_remote_sub',
    },
    {
      Icon: IconBatteryCharging,
      titleKey: 'hardware_first_spec_ultra_battery_row',
      subKey: 'hardware_first_spec_ultra_battery_row_sub',
    },
    { Icon: IconBase, titleKey: 'hardware_first_spec_ultra_paired', subKey: 'hardware_first_spec_ultra_paired_sub' },
    {
      Icon: IconConnected,
      titleKey: 'hardware_first_spec_extended_coverage',
      subKey: 'hardware_first_spec_ultra_coverage_sub',
    },
  ],
};

/** Fi Intelligence decodes dog behavior, so the cats page drops that row. */
function specRows(deviceKey: PickYourFiDeviceKey, species: Species): SpecRow[] {
  const rows = SPEC_ROWS[deviceKey];
  if (species !== Species.CAT) return rows;
  return rows.filter((row) => row.titleKey !== 'featuring_fi_intelligence');
}

export function HardwareFirstSpecs({
  deviceKey,
  species = Species.DOG,
}: {
  deviceKey: PickYourFiDeviceKey;
  species?: Species;
}) {
  const { t } = useTranslation();
  return (
    <section>
      <h2 className={styles.sectionHeading}>{t('hardware_first_device_specs', { device: DEVICE_NAMES[deviceKey] })}</h2>
      <div className={styles.specList}>
        {specRows(deviceKey, species).map((row) => (
          <div key={row.titleKey} className={styles.specRow}>
            <span className={styles.specIcon}>
              <row.Icon />
            </span>
            <span className={styles.specText}>
              <span className={styles.specTitle}>{t(row.titleKey)}</span>
              <span className={styles.specSub}>{t(row.subKey)}</span>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

// the figma gives mini and series 3+ one sentence-case list; ultra has its own
const DOG_TRACKER_FEATURE_KEYS = [
  'hardware_first_feature_gps_live',
  'hardware_first_feature_location_history',
  'hardware_first_feature_escape_geofence',
  'hardware_first_feature_activity_sleep',
  'hardware_first_feature_barking',
  'hardware_first_feature_scratching_licking',
  'hardware_first_feature_eating_drinking',
  'hardware_first_feature_vet_records',
  'hardware_first_feature_walks_car_rides',
  'hardware_first_feature_unlimited_users',
];

const FEATURE_KEYS: Record<PickYourFiDeviceKey, string[]> = {
  mini: DOG_TRACKER_FEATURE_KEYS,
  s3: DOG_TRACKER_FEATURE_KEYS,
  ultra: [
    'hardware_first_feature_ultra_gps',
    'hardware_first_feature_location_history',
    'hardware_first_feature_ultra_callback',
    'hardware_first_feature_escape_geofence',
    'hardware_first_feature_walks_car_rides',
    'hardware_first_feature_unlimited_users',
  ],
};

// the cats list drops the dog behaviors and takes the cat wording the mini pdp already uses
const MINI_CAT_FEATURE_KEYS = [
  'hardware_first_feature_gps_live',
  'hardware_first_feature_location_history',
  'hardware_first_feature_escape_geofence',
  'active_time_and_rest_tracking',
  'hardware_first_feature_vet_records',
  'outings_and_car_rides',
  'hardware_first_feature_unlimited_users',
];

/** The device name the section headings carry; the cats page names its own treatment. */
function deviceName(deviceKey: PickYourFiDeviceKey, species: Species): string {
  return species === Species.CAT ? 'Fi Mini for Cats' : DEVICE_NAMES[deviceKey];
}

export function HardwareFirstAllFeatures({
  deviceKey,
  species = Species.DOG,
}: {
  deviceKey: PickYourFiDeviceKey;
  species?: Species;
}) {
  const { t } = useTranslation();
  const featureKeys = species === Species.CAT ? MINI_CAT_FEATURE_KEYS : FEATURE_KEYS[deviceKey];
  return (
    <section className={styles.featuresCard}>
      <h2 className={styles.featuresHeading}>
        {t('hardware_first_all_device_features', { device: deviceName(deviceKey, species) })}
      </h2>
      <div className={styles.featureList}>
        {featureKeys.map((key) => (
          <div key={key} className={styles.featureRow}>
            <IconCheckCircle className={styles.featureCheck} />
            <span className={styles.featureLabel}>{t(key)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

interface Testimonial {
  quoteKey: string;
  nameKey: string;
}

const JOSH: Testimonial = {
  quoteKey: 'hardware_first_review_josh_quote',
  nameKey: 'hardware_first_review_josh_name',
};

// each device shows reviews of that device; the ultra set is generic fi, since starlink is new
// enough that named-product quotes are thin
const TESTIMONIALS: Record<PickYourFiDeviceKey, Testimonial[]> = {
  mini: [
    { quoteKey: 'hardware_first_review_lynn_quote', nameKey: 'hardware_first_review_lynn_name' },
    { quoteKey: 'hardware_first_review_michelle_quote', nameKey: 'hardware_first_review_michelle_name' },
    JOSH,
  ],
  s3: [
    { quoteKey: 'hardware_first_review_malou_quote', nameKey: 'hardware_first_review_malou_name' },
    JOSH,
    { quoteKey: 'hardware_first_review_christopher_quote', nameKey: 'hardware_first_review_christopher_name' },
  ],
  ultra: [
    { quoteKey: 'hardware_first_review_brad_quote', nameKey: 'hardware_first_review_brad_name' },
    { quoteKey: 'hardware_first_review_spencer_quote', nameKey: 'hardware_first_review_spencer_name' },
    { quoteKey: 'hardware_first_review_taralynn_quote', nameKey: 'hardware_first_review_taralynn_name' },
  ],
};

export function HardwareFirstOwnersSay({
  deviceKey,
  species = Species.DOG,
}: {
  deviceKey: PickYourFiDeviceKey;
  species?: Species;
}) {
  const { t } = useTranslation();
  const heading = t('hardware_first_what_device_owners_say', { device: deviceName(deviceKey, species) });
  const testimonials = TESTIMONIALS[deviceKey];
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // touch swipes the track natively; a mouse has to drag it, so the pointer drives scrollLeft
  // while snapping is off, and snapping returns once the release scroll has settled
  const drag = useRef<{ startX: number; startScrollLeft: number } | null>(null);
  const snapTimer = useRef<number | undefined>(undefined);
  const [snapOff, setSnapOff] = useState(false);
  useEffect(() => () => window.clearTimeout(snapTimer.current), []);

  const restoreSnap = () => {
    window.clearTimeout(snapTimer.current);
    snapTimer.current = window.setTimeout(() => setSnapOff(false), 150);
  };

  const onTrackScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    setActiveIndex(Math.round(track.scrollLeft / track.clientWidth));
    if (snapOff && !drag.current) restoreSnap();
  };

  const scrollToIndex = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (!track || event.pointerType !== 'mouse' || event.button !== 0) return;
    // stops the quote text from being selected as the pointer moves
    event.preventDefault();
    track.setPointerCapture(event.pointerId);
    drag.current = { startX: event.clientX, startScrollLeft: track.scrollLeft };
    window.clearTimeout(snapTimer.current);
    setSnapOff(true);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (!track || !drag.current) return;
    track.scrollLeft = drag.current.startScrollLeft - (event.clientX - drag.current.startX);
  };

  const onPointerUp = () => {
    const track = trackRef.current;
    if (!track || !drag.current) return;
    drag.current = null;
    scrollToIndex(Math.round(track.scrollLeft / track.clientWidth));
    restoreSnap();
  };

  return (
    <section>
      <h2 className={styles.sectionHeading}>{heading}</h2>
      {/* the track scrolls horizontally and holds nothing focusable, so it has to take focus
          itself for a keyboard user to reach the reviews past the first card */}
      <div
        className={classNames(styles.reviewTrack, { [styles.reviewTrackDragging]: snapOff })}
        ref={trackRef}
        onScroll={onTrackScroll}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        tabIndex={0}
        role="group"
        aria-label={heading}
      >
        {testimonials.map((review) => (
          <div key={review.quoteKey} className={styles.reviewCard}>
            <div className={styles.reviewStars}>
              {Array.from({ length: 5 }).map((_, index) => (
                <IconStar key={index} className={styles.reviewStar} />
              ))}
            </div>
            <div className={styles.reviewQuote}>{t('hardware_first_quote_wrapped', { quote: t(review.quoteKey) })}</div>
            <div className={styles.reviewName}>{t(review.nameKey)}</div>
          </div>
        ))}
      </div>
      <div className={styles.reviewDots}>
        {testimonials.map((review, index) => (
          <button
            key={review.quoteKey}
            type="button"
            aria-label={t('rescue_testimonial_dot_label', { number: index + 1, total: testimonials.length })}
            className={index === activeIndex ? styles.reviewDotActive : styles.reviewDot}
            onClick={() => scrollToIndex(index)}
          />
        ))}
      </div>
    </section>
  );
}

export function HardwareFirstQuizBand() {
  const { t } = useTranslation();
  const history = useHistory();
  const launchQuiz = useLaunchQuiz();

  return (
    <section className={styles.quizBand}>
      <div className={styles.quizEyebrow}>{t('compare_v2_quiz_eyebrow')}</div>
      <h2 className={styles.quizTitle}>{t('hardware_first_quiz_band_title')}</h2>
      <div className={styles.quizSubtitle}>{t('hardware_first_quiz_band_subtitle')}</div>
      <button
        type="button"
        className={styles.quizPrimary}
        onClick={() => {
          quizQuestionPage.quizButtonTapped();
          launchQuiz();
        }}
      >
        {t('take_the_quiz')}
      </button>
      <button
        type="button"
        className={styles.quizSecondary}
        onClick={() => {
          productListingPage.comparisonCompareProducts();
          history.push(AppPaths.Compare);
        }}
      >
        {t('compare_all_products')}
      </button>
    </section>
  );
}
